<template>
  <v-container fill-height>
    <div v-if="!attendance" class="d-flex align-center justify-center my-10">
      <v-progress-circular color="primary" indeterminate />
    </div>

    <v-row v-else class="fill-height" justify="center" no-gutters>
      <v-col cols="12" sm="8" md="5" lg="4" xl="3">
        <CustomChat
          :key="chatKey"
          :chat="_chat"
          :attendant="{
            name: attendance.attendant.name,
            icon: 'mdi-account',
          }"
          :height="'80vh'"
          :finalizeLabel="'Finalizar Atendimento'"
          :readMode="readMode"
          :disabled="_requesterChatDisabled"
          :placeholder="''"
          :isTypingAttendant="isTypingAttendant"
          @send-message="handleSendMessage($event)"
          @finalize="finalizeAttendance()"
          @attach-file="uploadFile($event)"
          @attendance-typing="handleTyping($event)"
        />

        <div v-if="readMode" class="d-flex align-center justify-center mt-4">
          <v-btn @click="finalizeAttendance()" class="text-none" text>
            Finalizar
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { getAttendance } from "@/services/attendance";
import { uploadFile } from "@/services/files";
import { handleAlert, showError, handleFinishAttendance } from "@/utils";
import { eventBus } from "@/eventBus";
import CustomChat from "@/components/customChat/CustomChat";

export default {
  name: "Attendance",

  components: {
    CustomChat,
  },

  data() {
    return {
      currentReferralMessage: {},
      isTypingAttendant: false,
      interval: null,
      readMode: false,
      currentStatus: "",
      requesterChatEnabled: null,
      chat: {
        messages: [],
      },
      chatKey: Date.now(),
      transferringAppointment: false,
    };
  },

  mounted() {
    document.addEventListener("touchstart", this.handleTouchStart);
    document.addEventListener("touchmove", this.handleTouchMove, {
      passive: false,
    });

    this.handleAttendance();

    //eventBus.$on("appointments:transferred", this.onAppointmentTransferred);
    eventBus.$on("attendance:transferred", this.onTransferredToQueue);
    eventBus.$on("attendance:finished", this.onAttendanceFinished);
    eventBus.$on("attendance:typing", this.handleTypingIndicator);
  },

  beforeDestroy() {
    document.removeEventListener("touchstart", this.handleTouchStart);
    document.removeEventListener("touchmove", this.handleTouchMove);

    clearInterval(this.interval);

    //eventBus.$off("appointments:transferred", this.onAppointmentTransferred);
    eventBus.$off("attendance:transferred", this.onTransferredToQueue);
    eventBus.$off("attendance:finished", this.onAttendanceFinished);
    eventBus.$off("attendance:typing", this.handleTypingIndicator);
  },

  computed: {
    ...mapState("socket", [
      "attendance",
      "messages",
      "inAttendance",
      "inQueue",
      "code",
    ]),

    _code() {
      return this.$route.query.code || this.attendance?.code || this.code;
    },

    _chat() {
      return {
        ...this.chat,
        messages: this.handleNewReferredMessages(this.messages),
      };
    },

    /**
     * Resolvido pela API na hierarquia balcão > vara > comarca.
     *
     * Só bloqueia quando algum nível disse explicitamente que não. Sem
     * configuração a API devolve `undefined` e o solicitante continua podendo
     * escrever, que é como este client sempre funcionou — o default oposto vale
     * apenas no fork do TJ-PB. Ver ADR-008.
     */
    _requesterChatDisabled() {
      return this.requesterChatEnabled === false;
    },
  },

  watch: {
    attendance(val, oldVal) {
      if (!!oldVal && !val && !this.inAttendance) {
        const params = {
          attendanceID: oldVal._id,
          deskID: oldVal.desk._id,
        };
        const query = {
          code: oldVal.code || this._code,
        };

        this.handleAlert("Atendimento finalizado pelo atendente", "info");

        this.handleRating(params, query);
      }
    },
  },

  methods: {
    ...mapActions("socket", [
      "setAttendance",
      "joinAttendance",
      "finishAttendance",
      "sendMessage",
      "setMessages",
      "addMessage",
      "resetAttendance",
      "setInQueue",
      "setAppointment",
      "sendTyping",
    ]),

    handleNewReferredMessages(messages) {
      if (!messages.referralMessage) return messages;

      let referralMessageNew = null;
      if (messages.referralMessage) {
        referralMessageNew = {
          ...messages.referralMessage,
          author: messages.referralMessage.author || {},
          message:
            messages.referralMessage.message || messages.referralMessage.text,
        };
      } else if (messages.referralMessageID && this.currentChat.messages) {
        referralMessageNew = this.currentChat.messages.find(
          (msg) => msg._id === messages.referralMessageID
        );

        if (referralMessageNew) {
          referralMessageNew = {
            ...referralMessageNew,
            author: referralMessageNew.author || {},
            message: referralMessageNew.message || referralMessageNew.text,
          };
        }
      }
      return referralMessageNew;
    },

    handleSendMessage(data) {
      this.currentReferralMessage = data.referralMessage;

      const payload = {
        attendanceID: this.attendance._id,
        text: data.message,
        referralMessageID: data.referralMessage?._id || null,
      };

      this.sendMessage({
        data: payload,
        callback: (error) => {
          if (error) {
            throw new Error(error);
          }
        },
      });
    },

    handleTyping(data) {
      const payload = {
        protocol: data.protocol,
        typing: data.typing,
      };

      this.sendTyping({
        data: payload,
      });
    },

    handleTypingIndicator(data) {
      // Se veio do atendente (não é o solicitante)
      if (!data?.isSolicitant && data?.typing !== undefined) {
        this.isTypingAttendant = data.typing;
      }
    },

    async uploadFile(event) {
      try {
        const originalFile = event.file;
        const timestamp = Date.now();
        const extension = originalFile.name.split(".").pop();
        const baseName = originalFile.name.replace(/\.[^/.]+$/, "");
        const newName = `${baseName}-${timestamp}.${extension}`;

        const renamedFile = new File([originalFile], newName, {
          type: originalFile.type,
        });

        let formData = new FormData();
        formData.append("file", renamedFile);

        const { data } = await uploadFile(formData);
        this.handleSendMessage({
          message: data.publicUrl,
          referralMessage: event.referralMessage,
        });

        this.chatKey = Date.now();
      } catch (error) {
        this.handleAlert(
          error.response?.data?.message || error.data.message,
          "error"
        );
      }
    },

    handleAttendance() {
      this.getAttendance();

      this.interval = setInterval(() => {
        this.getAttendance();
      }, 60000);
    },

    async getAttendance() {
      try {
        const { data } = await getAttendance(this.attendance._id);

        this.requesterChatEnabled = data.requesterChatEnabled ?? null;

        if (!this.currentStatus) {
          this.currentStatus = data.status;
          return;
        }

        if (
          this.currentStatus === "in-progress" &&
          data.status === "finished"
        ) {
          this.handleAlert("Atendimento finalizado por inatividade", "info");

          const params = {
            attendanceID: data._id,
            deskID: data.desk._id,
          };
          const query = {
            code: data.code || this._code,
          };

          this.handleRating(params, query);
          return;
        }
      } catch (error) {
        this.handleAlert(
          error.response?.data?.message || error.data.message,
          "error"
        );

        this.resetAttendance();
        this.$router.push({ name: "Home" });
      }
    },

    handleJoin() {
      try {
        const payload = {
          protocol: this.attendance.protocol,
        };

        this.joinAttendance({
          data: payload,
          callback: (error, data) => {
            if (error) {
              throw new Error(error);
            }

            if (data.messages) {
              this.setMessages([]);

              data.messages.forEach((el) => {
                this.addMessage(el);
              });
            }
          },
        });
      } catch (error) {
        this.showError(error);
      }
    },

    finalizeAttendance() {
      const msg = {
        message: "Atendimento finalizado pelo solicitante",
      };

      const payload = {
        attendanceID: this.attendance._id,
        deskID: this.attendance.desk._id,
      };

      this.handleSendMessage(msg);
      this.handleFinishAttendance();

      this.handleRating(payload);
    },

    handleRating(params, query) {
      if (this.transferringAppointment) return;

      this.$router.push(
        {
          name: "Rating",
          params,
          query,
        },
        () => {},
        () => {}
      );
    },

    handleTouchStart(event) {
      this.startY = event.touches[0].clientY;
    },

    handleTouchMove(event) {
      const currentY = event.touches[0].clientY;

      if (currentY > this.startY && window.scrollY === 0) {
        event.preventDefault();
      }
    },

    onAppointmentCreated(data) {
      const appointment = data.appointment || data;

      if (appointment?.autoScheduled === true) {
        this.setAppointment(appointment);
        this.setInQueue(true);

        this.$router
          .push({ path: `/queue-appointments/${appointment._id}` })
          .catch(() => {});
      }
    },

    onTransferredToQueue(data) {
      const desk = data?.desk;
      const operationMode = desk?.operationMode;

      if (!operationMode || operationMode === "queue") {
        this.setInQueue(true);
        this.$router.push({ path: "/queue" }).catch(() => {});
      }
    },

    onAttendanceFinished(data) {
      if (data.skipRating) return;

      const params = {
        attendanceID: data._id,
        deskID: data.desk._id,
      };
      const query = {
        code: data.code || this._code,
      };

      this.handleAlert("Atendimento finalizado pelo atendente", "info");

      this.handleRating(params, query);
    },

    showError,
    handleAlert,
    handleFinishAttendance,
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
